"use client";

import { useEffect, useState } from "react";
import { doc, getDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import LegalPageLayout from "@/components/LegalPageLayout";
import SanitizedHtml from "@/components/SanitizedHtml";

/**
 * Renders one of the fixed-slug legal pages (privacy/terms/refunds) from the `pages` Firestore
 * collection, editable via the WYSIWYG editor at /admin/content/pages -- but starts from the
 * exact hardcoded copy the caller passes in as `fallback*`, so a visitor sees identical content
 * to before this existed on the very first paint, and nothing changes at all until an admin
 * actually edits and saves this slug. A client-only fetch after that static shell paints, same
 * pattern as BlogView/FaqSection -- no server-side Firestore read, so this doesn't turn these
 * pages into a per-request Cloud Run hit the way rendering them dynamically server-side would.
 * The page's <title>/description/canonical stay hardcoded in its own page.tsx `metadata` export
 * regardless of what this fetches, so SEO metadata is entirely unaffected by the content itself
 * being editable.
 */
export default function CmsPageView({
  slug,
  fallbackTitle,
  fallbackUpdated,
  fallbackBodyHtml,
}: {
  slug: string;
  fallbackTitle: string;
  fallbackUpdated: string;
  fallbackBodyHtml: string;
}) {
  const [title, setTitle] = useState(fallbackTitle);
  const [updated, setUpdated] = useState(fallbackUpdated);
  const [bodyHtml, setBodyHtml] = useState(fallbackBodyHtml);

  useEffect(() => {
    getDoc(doc(db, "pages", slug))
      .then((snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        if (typeof data.title === "string" && data.title.trim()) setTitle(data.title);
        if (typeof data.bodyHtml === "string" && data.bodyHtml.trim()) setBodyHtml(data.bodyHtml);
        if (data.updatedAt instanceof Timestamp) {
          setUpdated(data.updatedAt.toDate().toLocaleDateString("en-IN", { dateStyle: "long" }));
        }
      })
      .catch(() => {
        // Firestore hiccup -- the hardcoded fallback above is already rendered, so a visitor
        // never sees an error state on a legal/compliance page over this.
      });
  }, [slug]);

  return (
    <LegalPageLayout title={title} updated={updated}>
      <SanitizedHtml html={bodyHtml} />
    </LegalPageLayout>
  );
}
